import { ReactNode, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { Sidebar } from './Sidebar';
import { Navbar } from './Navbar';
import { ProjectHeader } from './ProjectHeader';
// Board view uses Kanban page which handles drag-and-drop and workflow status updates
import { Kanban } from '../pages/Kanban';
import { BacklogView } from './BacklogView';
import { Summary } from '../pages/Summary';
import { ProjectList } from '../pages/ProjectList';
import { ProjectMembersPage } from '../pages/ProjectMembersPage';
import { Sprints } from '../pages/Sprints';
import Timeline from '@/pages/Timeline';
import { Calendar } from '../pages/Calendar';
import { fetchProject, setCurrentProject, clearCurrentProject } from '@/store/projectSlice';

export const AppLayout = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const params = useParams();
  const dispatch = useAppDispatch();
  const { currentProject, projects } = useAppSelector((state) => state.projects);
  const projectHeaderFullscreen =
    useAppSelector((state) => ((state as any).ui?.projectHeaderFullscreen as boolean) ?? false);
  const authMode = useAppSelector((state) => state.auth.mode);
  
  // Show ProjectHeader when a project is selected
  const showProjectHeader = !!currentProject;
  const isHeaderFullscreen = showProjectHeader && projectHeaderFullscreen;
  const hideChrome = isHeaderFullscreen;
  const hideMainHeader = authMode === 'embed';

  // Handle navigation and project state
  useEffect(() => {
    const projectId = params.projectId;
    const isProjectRoute = location.pathname.includes('/projects/') && projectId;

    if (!isProjectRoute) {
      if (currentProject) dispatch(clearCurrentProject());
      return;
    }

    // Avoid duplicate fetch loops: if currentProject already matches, do nothing
    if (currentProject && currentProject.id === projectId) return;

    // Check if project exists in projects list (prevents fetching deleted projects)
    const existingProject = projects.find(p => p.id === projectId);
    if (existingProject) {
      dispatch(setCurrentProject(existingProject));
    } else {
      // Only fetch if projects list has been loaded (length > 0 means it was fetched)
      // If project doesn't exist in list after fetch, it might be deleted
      if (projects.length > 0) {
        // Project not found in list - might be deleted, don't fetch
        return;
      }
      // Projects list not loaded yet, safe to fetch
      dispatch(fetchProject(projectId));
    }
  }, [params.projectId, location.pathname, projects, currentProject?.id, dispatch]);

  const renderProjectContent = () => {
    if (location.pathname.includes('/board')) return <Kanban />;
    if (location.pathname.includes('/backlog')) return <BacklogView />;
    if (location.pathname.includes('/summary')) return <Summary />;
    if (location.pathname.includes('/sprints')) return <Sprints />;
    if (location.pathname.includes('/people')) return <ProjectMembersPage />;
    if (location.pathname.includes('/list')) return <ProjectList />;
    if (location.pathname.includes('/timeline')) return <Timeline />;
    if (location.pathname.includes('/calendar')) return <Calendar />;

    return (
      <div className="text-center py-12 text-gray-500">
        <h2 className="text-xl font-semibold mb-2">
          {location.pathname.includes('/reports') && 'Project Reports'}
          {location.pathname.includes('/forms') && 'Project Forms'}
          {location.pathname.includes('/goals') && 'Project Goals'}
          {location.pathname.includes('/development') && 'Development'}
          {location.pathname.includes('/code') && 'Code'}
          {location.pathname.includes('/archived') && 'Archived Work Items'}
          {location.pathname.includes('/pages') && 'Pages'}
          {![
            '/reports',
            '/forms',
            '/goals',
            '/development',
            '/code',
            '/archived',
            '/pages',
          ].some((segment) => location.pathname.includes(segment)) && 'Coming Soon'}
        </h2>
        <p>This section is coming soon!</p>
      </div>
    );
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50 overflow-hidden">
      {!hideChrome && !hideMainHeader && <Navbar />}
      <div className="flex flex-1 min-w-0 overflow-hidden">
        {!hideChrome && <Sidebar />}
        <main className="flex-1 min-w-0 overflow-x-hidden overflow-y-auto">
          {showProjectHeader ? (
            isHeaderFullscreen ? (
              <div className="flex flex-col h-full bg-white">
                <ProjectHeader 
                  projectName={currentProject.name}
                  projectIcon={currentProject.key?.charAt(0) || 'P'}
                  projectColor="bg-blue-500"
                  projectId={currentProject.id}
                  projectDescription={currentProject.description || ''}
                />
                <div className="flex-1 overflow-y-auto px-6 sm:px-8 lg:px-10 py-6 bg-white">
                  {renderProjectContent()}
                </div>
              </div>
            ) : (
              <div>
                <ProjectHeader 
                  projectName={currentProject.name}
                  projectIcon={currentProject.key?.charAt(0) || 'P'}
                  projectColor="bg-blue-500"
                  projectId={currentProject.id}
                  projectDescription={currentProject.description || ''}
                />
                <div className="w-full max-w-full overflow-x-hidden min-w-0">
                  <div className="px-2 sm:px-4 lg:px-6 py-4 sm:py-6 h-full flex flex-col min-w-0">
                    {renderProjectContent()}
                  </div>
                </div>
              </div>
            )
          ) : (
            <div className="px-4 sm:px-6 lg:px-8 py-6">
              {children}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};
